import {TRPC_PROXY_CLIENT} from "@frontend/utils/trpcClient"
import type {TrackEventName} from "@ri2/db/client"

export {TRACK_EVENT_NAMES, type TrackEventName} from "@ri2/db/client"

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void
  }
}

const LOCAL_STORAGE_WIDGET_CASE_ID_KEY = "ri2-widget-caseId"

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const toValidCaseId = (value: string | null | undefined): string | null => {
  if (!value) return null

  const trimmed = value.trim()
  return UUID_REGEX.test(trimmed) ? trimmed : null
}

const getCaseIdFromHashRoute = (): string | null => {
  const routeMatch = window.location.hash.match(
    /(?:^#\/?|#)ri2\/([0-9a-f-]{36})(?:\/|$)/i,
  )

  return toValidCaseId(routeMatch?.[1])
}

const getCaseIdFromHashParam = (): string | null => {
  const paramMatch = window.location.hash.match(/(?:^#|&)ri2-case=([^&]+)/)

  if (!paramMatch) return null

  try {
    return toValidCaseId(decodeURIComponent(paramMatch[1]))
  } catch {
    return null
  }
}

const getCaseIdFromLocalStorage = (): string | null => {
  try {
    return toValidCaseId(localStorage.getItem(LOCAL_STORAGE_WIDGET_CASE_ID_KEY))
  } catch {
    return null
  }
}

const getTrackingCaseId = (caseId?: string): string | null =>
  toValidCaseId(caseId) ||
  getCaseIdFromHashRoute() ||
  getCaseIdFromHashParam() ||
  getCaseIdFromLocalStorage()

export const sendToGA = (eventName: TrackEventName): void => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({event: eventName})
}

export const sendToDbTracking = (
  eventName: TrackEventName,
  caseId?: string,
): void => {
  const trackedCaseId = getTrackingCaseId(caseId)
  if (!trackedCaseId) return

  void TRPC_PROXY_CLIENT.trackCaseEvent
    .mutate({
      caseId: trackedCaseId,
      eventName,
    })
    .catch((error: unknown) => {
      console.error("Failed to persist tracking event", eventName, error)
    })
}

export const callClarity = (...args: unknown[]): boolean => {
  if (typeof window === "undefined" || typeof window.clarity !== "function")
    return false

  try {
    window.clarity(...args)
    return true
  } catch {
    return false
  }
}

export const trackClarityEvent = (eventName: string, caseId?: string): void => {
  if (!eventName) return

  const trackedCaseId = getTrackingCaseId(caseId)
  if (trackedCaseId) callClarity("set", "caseId", trackedCaseId)

  callClarity("event", eventName)
}

export const trackEvent = (
  eventName: TrackEventName,
  caseId?: string,
): void => {
  sendToGA(eventName)
  trackClarityEvent(eventName, caseId)
  sendToDbTracking(eventName, caseId)
}
